import React from "react";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import {
  MarkAsCompleted,
  MarkAsCompletedRef,
} from "@/modules/meetings/components/meetings/mark-as-completed";
import { TasksResponse, Task, AddTaskRequest } from "@/modules/tasks/dtos";

interface MeetingCompletionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  task: TasksResponse | Task | null;
  confirmButtonText: string;
  isLoading: boolean;
  hasIncompleteTasks: boolean;
  tasksReadyForEvaluation: boolean;
  markAsCompletedRef: React.RefObject<MarkAsCompletedRef>;
  onTasksChange: (tasks: AddTaskRequest[]) => void;
  onValidationChange: (hasIncomplete: boolean) => void;
  onTasksNeedEvaluation: () => void;
  onTasksReadyForEvaluation: (ready: boolean) => void;
}

export const MeetingCompletionModal: React.FC<MeetingCompletionModalProps> = ({
  open,
  onClose,
  onConfirm,
  task,
  confirmButtonText,
  isLoading,
  hasIncompleteTasks,
  tasksReadyForEvaluation,
  markAsCompletedRef,
  onTasksChange,
  onValidationChange,
  onTasksNeedEvaluation,
  onTasksReadyForEvaluation,
}) => {
  const screen = useScreenSize();

  if (!task) return null;

  const modalContent = (
    <MarkAsCompleted
      ref={markAsCompletedRef}
      meeting={task as TasksResponse}
      onTasksChange={onTasksChange}
      onValidationChange={onValidationChange}
      onTasksNeedEvaluation={onTasksNeedEvaluation}
      onTasksReadyForEvaluation={onTasksReadyForEvaluation}
    />
  );

  const isConfirmDisabled =
    (hasIncompleteTasks && confirmButtonText === "Complete") ||
    (confirmButtonText === "Evaluate" && !tasksReadyForEvaluation);

  if (screen.isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={onClose}
        title="Complete Meeting"
        cancelButtonText="Cancel"
        confirmButtonText={confirmButtonText}
        onConfirm={onConfirm}
        isLoading={isLoading}
        isConfirmDisabled={isConfirmDisabled}
      >
        {modalContent}
      </MobileModal>
    );
  }

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Complete Meeting"
      cancelButtonText="Cancel"
      confirmButtonText={confirmButtonText}
      onConfirm={onConfirm}
      isLoading={isLoading}
      isConfirmDisabled={isConfirmDisabled}
      sx={{
        width: "750px",
      }}
    >
      {modalContent}
    </CommonModal>
  );
};
